import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';
import { selectCurrentUser } from '../../features/auth/authSlice';
import { useLogoutMutation } from '../../features/auth/authApi';
import LogoIcon from '../../assets/logo.svg';
import NotificationsBell from './NotificationsBell';

function initialsFor(user) {
  const source = user?.full_name || user?.email || '?';
  return source
    .trim()
    .split(/\s+/)
    .map((part) => part[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

export default function Topbar({ onMenuClick }) {
  const user = useSelector(selectCurrentUser);
  const [logout, { isLoading }] = useLogoutMutation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [menuOpen]);

  const handleLogout = async () => {
    setMenuOpen(false);
    await logout();
    navigate('/login', { replace: true });
  };

  return (
    <header className="h-16 border-b border-border bg-background/80 backdrop-blur-sm flex items-center px-4 sm:px-6 sticky top-0 z-10 gap-2">
      <button
        onClick={onMenuClick}
        aria-label="Open menu"
        className="md:hidden -ml-1 h-9 w-9 shrink-0 flex items-center justify-center rounded-lg text-foreground-muted hover:text-foreground hover:bg-background-elevated transition-colors"
      >
        <svg viewBox="0 0 24 24" fill="none" className="h-5 w-5">
          <path d="M3 6h18M3 12h18M3 18h18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      </button>

      <Link to="/" className="md:hidden flex items-center gap-2 shrink-0">
        <img src={LogoIcon} alt="" className="h-6 w-6" />
        <span className="font-semibold text-foreground text-sm">Cavan Capital</span>
      </Link>

      <div className="flex-1" />

      <div className="flex items-center gap-2 sm:gap-4">
        <NotificationsBell />

        <div className="relative" ref={menuRef}>
          <button
            onClick={() => setMenuOpen((o) => !o)}
            aria-label="Account menu"
            aria-expanded={menuOpen}
            className="flex items-center gap-2 rounded-lg px-1.5 py-1 hover:bg-background-elevated transition-colors"
          >
            <span className="hidden sm:block text-right leading-tight">
              <p className="text-sm font-medium text-foreground">{user?.full_name}</p>
              <p className="text-xs text-foreground-subtle">{user?.email}</p>
            </span>
            <span className="h-8 w-8 rounded-full bg-accent-soft text-accent flex items-center justify-center text-xs font-semibold shrink-0">
              {initialsFor(user)}
            </span>
          </button>

          {menuOpen && (
            <div className="absolute right-0 mt-2 w-56 rounded-xl border border-border bg-background-surface shadow-card-hover z-20 overflow-hidden">
              <div className="px-4 py-3 border-b border-border sm:hidden">
                <p className="text-sm font-medium text-foreground truncate">{user?.full_name}</p>
                <p className="text-xs text-foreground-subtle truncate">{user?.email}</p>
              </div>
              <Link
                to="/change-password"
                onClick={() => setMenuOpen(false)}
                className="block px-4 py-2.5 text-sm text-foreground hover:bg-background-elevated transition-colors"
              >
                Change password
              </Link>
              <button
                onClick={handleLogout}
                disabled={isLoading}
                className="w-full text-left px-4 py-2.5 text-sm text-danger hover:bg-background-elevated transition-colors disabled:opacity-50"
              >
                Log out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
